<template lang="pug">
v-dialog(v-model="dialog" persistent max-width="400px" :fullscreen="$vuetify.breakpoint.xsOnly")
  v-card.d-flex.flex-column(:style="$vuetify.breakpoint.xsOnly ? 'height: 100%;' : ''")
    v-card-title.pa-0.pt-3.pr-3
      v-toolbar(dense flat)

    //- Оплата не прошла: отклонена покупателем, истекло время или ошибка сервиса
    template(v-if="errorTitle")
      v-container.d-flex.flex-column.flex-grow-1.justify-center.text-center
        div
          v-icon(size="80" color="error") mdi-close-circle-outline
          .text-h5.font-weight-bold.mt-6 {{ errorTitle }}
          .subtitle-1.mt-2.text--secondary(v-if="errorText") {{ errorText }}
      v-card-actions.pa-4.mb-12
        v-btn.text-h5.font-weight-bold(block color="primary" @click="dialog = false") {{ $t("закрыть") }}

    v-container.d-flex.flex-column.flex-grow-1.justify-center(v-else style="overflow-y: auto;")
      //- Оплата подтверждена - показываем галочку, потом уходим фискализировать чек
      template(v-if="success")
        .text-center.mb-16
          svg.re-success-check(viewBox="0 0 52 52")
            circle.re-success-check__circle(cx="26" cy="26" r="24" fill="none")
            path.re-success-check__mark(fill="none" d="M14 27l8 8 16-16")
          .text-h5.font-weight-bold.mt-8 {{ $t("оплата_прошла_успешно") }}
          .re-number-font.text-h4.mt-2 {{ amount | numeral('0,0.[00]') }} ₸

      //- Создание QR
      template(v-else-if="loading")
        .text-center.mb-16
          v-progress-circular(indeterminate color="secondary" size="64")
          .subtitle-1.my-6 {{ $t("создание_qr_кода") }}...

      //- QR отсканирован - ждём подтверждения оплаты покупателем
      template(v-else-if="awaitingConfirmation")
        .text-center.mb-16
          v-progress-circular(indeterminate color="primary" size="64" width="4")
          .text-h5.font-weight-bold.mt-8 {{ $t("ожидайте_подтверждения") }}
          .subtitle-1.mt-2.text--secondary {{ $t("покупателю_необходимо_подтвердить_оплату") }}
          div.mt-6
            v-btn.text-subtitle-1(text color="primary" :loading="rejecting" @click="onCancelPayment()") {{ $t("отменить_оплату") }}

      //- QR для оплаты
      template(v-else-if="qrcode")
        //- Смещаем блок чуть выше центра
        .mb-16
          v-row
            v-col
              .re-number-font.text-h1.text-center {{ amount | numeral('0,0.[00]') }} ₸

          v-row.mt-4
            v-col
              v-row(justify="center")
                img.halyk-qrcode-img(src="/static/halyk-logo.png" height="26" width="26")
                div.halyk-corners
                  div.halyk-qrcode
                    qrcode(:value="qrcode" size="180" level="H" renderAs="svg")

          v-row.mt-6
            v-col.px-6
              v-list(style="background-color: var(--v-reBackground-base) !important")
                v-list-item.re-v-list-fulltext
                  v-list-item-icon.align-self-center.my-0
                    img.ml-2(:src="$vuetify.theme.dark ? '/static/logo-black.png' : '/static/logo-white.png'" width="50")
                  v-list-item-content
                    v-list-item-title.font-weight-bold {{ $t('единый_qr') }}
                    v-list-item-title.text-wrap {{ $t("оплати_через_мобильное_приложение_любого_банка") }}

          v-row.mt-2
            v-col.text-center
              v-btn.text-subtitle-1(text color="primary" :loading="rejecting" @click="onCancelPayment()") {{ $t("отменить_оплату") }}

</template>
<script>
import { Decimal } from 'decimal.js'
import { mapActions, mapState } from 'vuex'
import qrcode from 'qrcode.vue'

const POLL_INTERVAL = 1500
// Сколько ждём, пока покупатель отсканирует QR
const SCAN_TIMEOUT = 180000
// Сколько ждём подтверждение оплаты после сканирования
const CONFIRM_TIMEOUT = 120000
// Сколько держим экран успеха перед фискализацией
const SUCCESS_DURATION = 2000

export default {
  components: {
    qrcode,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    cashRegister: {
      type: Object,
      required: true,
    },
    organization: {
      type: Object,
      required: true,
    },
    amount: {
      type: Number,
      required: true,
    },
  },

  data: () => ({
    loading: false,
    errorTitle: null,
    errorText: null,
    qrcode: null,
    billNumber: null,
    status: null,
    scanned: false,
    statusInterval: null,
    qrShownAt: null,
    success: false,
    successTimeout: null,
    rejecting: false,
    rejectRaceHandled: false,
    unloadAuth: null,
    pollStartedAt: null,
    statusLoading: false,
  }),

  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),

    dialog: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },

    // API требует телефон кассира строго в формате 87XXXXXXXXX (11 цифр, без +7)
    cashierPhoneNumber() {
      const digits = (this.user.phone || '').replace(/\D/g, '')
      if (digits.length === 11 && digits.startsWith('7')) {
        return `8${digits.slice(1)}`
      }
      return digits
    },

    // Покупатель отсканировал QR - остаётся подтвердить оплату в приложении банка
    awaitingConfirmation() {
      return ['SCANNED', 'BLOCKED', 'AUTH'].includes(this.status)
    },
  },

  watch: {
    value(opened) {
      if (opened) {
        this.start()
      } else {
        this.reset()
      }
    },
  },

  beforeDestroy() {
    this.stopPolling()
    this.clearSuccessTimeout()
    this.detachPageHide()
  },

  methods: {
    ...mapActions({
      generateDynamicQr: 'halykQr/generateDynamicQr',
      getQrStatus: 'halykQr/getQrStatus',
      rejectQr: 'halykQr/rejectQr',
      getBearerToken: 'auth/getBearerToken',
      showConfirm: 'confirm/showConfirm',
      showSnackbar: 'tools/showSnackbar',
      analyticsLogEvent: 'analytics/logEvent',
    }),

    // Событие воронки Единого QR: сумма всегда, длительность - от момента показа QR
    logQrEvent(eventName, eventProperties) {
      const properties = { amount: this.amount, ...eventProperties }
      if (this.qrShownAt) {
        properties.duration_sec = Math.round((Date.now() - this.qrShownAt) / 1000)
      }
      this.analyticsLogEvent({ eventName, eventProperties: properties })
    },

    reset() {
      this.stopPolling()
      this.clearSuccessTimeout()
      this.success = false
      this.loading = false
      this.errorTitle = null
      this.errorText = null
      this.qrcode = null
      this.billNumber = null
      this.status = null
      this.scanned = false
      this.pollStartedAt = null
      this.statusLoading = false
      this.rejecting = false
      this.rejectRaceHandled = false
      this.detachPageHide()
      this.qrShownAt = null
    },

    async start() {
      this.stopPolling()
      this.errorTitle = null
      this.errorText = null
      this.qrcode = null
      this.billNumber = null
      this.status = null
      this.scanned = false
      this.qrShownAt = null
      this.loading = true

      this.logQrEvent('re_halyk_qr_payment_started')

      if (!/^87\d{9}$/.test(this.cashierPhoneNumber)) {
        this.loading = false
        this.errorTitle = this.$t('halykQr/INVALID_PHONE')
        this.logQrEvent('re_halyk_qr_payment_failed', { stage: 'CREATE', error_code: 'INVALID_PHONE' })
        return
      }

      try {
        const { data } = await this.generateDynamicQr({
          cashRegisterId: this.cashRegister.id,
          amount: this.amount,
          pointOfSale: (this.cashRegister.pos && this.cashRegister.pos.address) || this.cashRegister.name,
          merchantName: this.organization.businessName,
          phoneNumber: this.cashierPhoneNumber,
        })
        // qrcode - короткая ссылка (не EMV-payload), сумму из него не достать,
        // поэтому до отображения QR разово сверяем сумму счёта через статус
        const { data: statusData } = await this.getQrStatus({ billNumber: data.billNumber })
        if (!this.amountMatches(statusData.amount)) {
          this.loading = false
          this.errorTitle = this.$t('сумма_qr_не_совпадает')
          this.logQrEvent('re_halyk_qr_payment_failed', { stage: 'CREATE', error_code: 'AMOUNT_MISMATCH' })
          return
        }

        this.qrcode = data.qrcode
        this.billNumber = data.billNumber
        this.status = data.status || 'NEW'
        this.loading = false
        this.qrShownAt = Date.now()
        this.logQrEvent('re_halyk_qr_code_shown')
        this.attachPageHide()
        this.startPolling()
      } catch (error) {
        this.loading = false
        this.setError(error)
        this.logQrEvent('re_halyk_qr_payment_failed', { stage: 'CREATE', error_code: this.errorCode(error) })
      }
    },

    // Даём кассиру увидеть подтверждение, и только потом отдаём чек на фискализацию
    showSuccess(done) {
      this.success = true
      this.successTimeout = setTimeout(() => {
        this.successTimeout = null
        done()
        this.dialog = false
      }, SUCCESS_DURATION)
    },

    startPolling() {
      this.pollStartedAt = Date.now()
      this.statusInterval = setInterval(() => this.pollStatus(), POLL_INTERVAL)
    },

    clearSuccessTimeout() {
      if (this.successTimeout) {
        clearTimeout(this.successTimeout)
        this.successTimeout = null
      }
    },

    stopPolling() {
      if (this.statusInterval) {
        clearInterval(this.statusInterval)
        this.statusInterval = null
      }
    },

    async pollStatus() {
      if (this.statusLoading || !this.billNumber) {
        return
      }

      if (this.pollStartedAt && Date.now() - this.pollStartedAt > (this.scanned ? CONFIRM_TIMEOUT : SCAN_TIMEOUT)) {
        this.stopPolling()
        const wasScanned = this.scanned
        const rejectResult = await this.tryReject('TIMEOUT')

        // Погасить не успели - покупатель уже оплачивает: даём оплате дойти вместо отмены
        if (rejectResult === 'IN_PROGRESS' && !this.rejectRaceHandled) {
          this.rejectRaceHandled = true
          this.scanned = true
          this.startPolling()
          return
        }

        this.errorTitle = this.$t('покупка_отменена')
        this.errorText = this.$t(wasScanned ? 'покупатель_не_подтвердил_оплату' : 'покупатель_не_отсканировал_qr')
        this.logQrEvent('re_halyk_qr_payment_timeout', { stage: wasScanned ? 'CONFIRM' : 'SCAN' })
        return
      }

      this.statusLoading = true
      try {
        const { data } = await this.getQrStatus({ billNumber: this.billNumber })
        this.status = data.status

        // QR отсканирован - на подтверждение оплаты даём отдельное время
        if (this.awaitingConfirmation && !this.scanned) {
          this.scanned = true
          this.pollStartedAt = Date.now()
        }

        if (data.status === 'PAID') {
          this.stopPolling()
          if (!this.amountMatches(data.amount)) {
            this.errorTitle = this.$t('сумма_оплаты_не_совпадает')
            this.logQrEvent('re_halyk_qr_payment_failed', { stage: 'PAID', error_code: 'AMOUNT_MISMATCH' })
            return
          }
          this.logQrEvent('re_halyk_qr_payment_paid')
          this.showSuccess(() => this.$emit('paid', data))
        } else if (data.status === 'REJECTED') {
          this.stopPolling()
          this.errorTitle = this.$t('покупка_отменена')
          this.errorText = this.$t('покупатель_не_подтвердил_оплату')
          this.logQrEvent('re_halyk_qr_payment_rejected')
        }
      } catch (error) {
        // Не прерываем опрос на временных ошибках статуса
        console.debug(error)
      } finally {
        this.statusLoading = false
      }
    },

    // «Назад» (Android backbutton, браузерная кнопка) перехватывается guard'ом
    // TicketDraft.beforeRouteLeave и попадает сюда - реакция зависит от состояния
    onBackPressed() {
      if (this.errorTitle) {
        // Флоу завершён - закрываем диалог, повторное «назад» уведёт со страницы
        this.dialog = false
        return
      }
      if (this.success || this.loading) {
        // Успех ждёт фискализации, при создании QR ещё нет billNumber - игнорируем
        return
      }
      this.onCancelPayment()
    },

    // Веб: закрытие вкладки/рефреш гасит QR keepalive-запросом - обычный axios
    // при выгрузке страницы не доживает, sendBeacon не умеет Authorization
    async attachPageHide() {
      if (this.$isCordova()) {
        return
      }
      try {
        this.unloadAuth = await this.getBearerToken()
      } catch (error) {
        this.unloadAuth = null
      }
      window.addEventListener('pagehide', this.onPageHide)
    },

    detachPageHide() {
      window.removeEventListener('pagehide', this.onPageHide)
    },

    onPageHide() {
      if (!this.billNumber || !this.dialog || this.success || this.errorTitle || !this.unloadAuth) {
        return
      }
      const headers = {
        Authorization: `Bearer ${this.unloadAuth.bearerToken}`,
        'x-user-uid': this.unloadAuth.userUid,
        'Content-Type': 'application/json',
      }
      if (this.unloadAuth.impersonateSlackId) {
        headers['x-impersonate-slack-id'] = this.unloadAuth.impersonateSlackId
      }
      fetch(`${import.meta.env.VITE_API_ROOT_URL || ''}/halyk-qr/api/qr/${this.billNumber}/reject`, {
        method: 'POST',
        keepalive: true,
        headers,
        body: JSON.stringify({ reason: 'CLOSE' }),
      }).catch(() => {})
    },

    // Гасим QR у банка, чтобы его нельзя было оплатить после отмены.
    // IN_PROGRESS - банк отказал, потому что покупатель уже начал оплату
    async tryReject(from) {
      if (!this.billNumber) {
        return 'ERROR'
      }
      try {
        await this.rejectQr({ billNumber: this.billNumber, reason: from })
        this.logQrEvent('re_halyk_qr_qr_rejected', { from, outcome: 'REJECTED' })
        return 'REJECTED'
      } catch (error) {
        const outcome = this.errorCode(error) === 'HALYK_HTTP' ? 'IN_PROGRESS' : 'ERROR'
        this.logQrEvent('re_halyk_qr_qr_rejected', { from, outcome })
        return outcome
      }
    },

    onCancelPayment() {
      this.showConfirm({
        icon: 'mdi-close-circle-outline',
        title: this.$t('отменить_оплату_вопрос'),
        text: this.$t('qr_будет_погашен_покупатель_не_сможет_оплатить'),
        resolveText: this.$t('да_отменить'),
        rejectText: this.$t('нет'),
      }).then(async () => {
        // Пока confirm был открыт, оплата могла завершиться - тогда гасить нечего
        if (this.success || this.errorTitle) {
          return
        }
        this.rejecting = true
        const rejectResult = await this.tryReject('CLOSE')
        this.rejecting = false

        if (rejectResult === 'IN_PROGRESS') {
          // Покупатель уже оплачивает - остаёмся и ждём исход
          this.showSnackbar({ message: this.$t('отменить_нельзя_покупатель_уже_завершает_оплату') })
          this.scanned = true
          this.pollStartedAt = Date.now()
          return
        }
        this.dialog = false
      }).catch(() => {})
    },

    // Код ошибки сервиса: для аналитики и для выбора текста
    errorCode(error) {
      const err = error && error.response && error.response.data && error.response.data.error
      return (err && err.code) || 'UNKNOWN'
    },

    // Известную ошибку показываем как есть, неизвестную (сеть, 5xx) - как недоступность сервиса
    setError(error) {
      const err = error && error.response && error.response.data && error.response.data.error
      if (err && err.code && this.$te(`halykQr/${err.code}`)) {
        this.errorTitle = this.$t(`halykQr/${err.code}`)
        this.errorText = err.errDescription || null
      } else {
        this.errorTitle = this.$t('сервис_единого_qr_недоступен')
        this.errorText = this.$t('пожалуйста_попробуйте_позже_еще_раз')
      }
    },

    amountMatches(value) {
      if (value === null || value === undefined || value === '') {
        return false
      }
      try {
        return new Decimal(value).equals(new Decimal(this.amount))
      } catch (error) {
        return false
      }
    },
  },
}
</script>

<style lang="stylus" scoped>
.re-success-check
  width 96px
  height 96px
  stroke var(--v-success-base)
  stroke-width 3
  stroke-linecap round
  stroke-linejoin round
  animation re-success-pulse 1.6s ease-in-out 0.9s infinite

.re-success-check__circle
  stroke-dasharray 151
  stroke-dashoffset 151
  animation re-success-draw 0.6s ease-out forwards

.re-success-check__mark
  stroke-dasharray 36
  stroke-dashoffset 36
  animation re-success-draw 0.35s ease-out 0.5s forwards

@keyframes re-success-draw
  to
    stroke-dashoffset 0

@keyframes re-success-pulse
  0%, 100%
    transform scale(1)
  50%
    transform scale(1.04)

.halyk-qrcode-img
  position absolute
  margin-top 95px

.halyk-qrcode
  display inline-block
  padding 8px 8px 1px 8px
  background #fffffe
  background-image linear-gradient(#fffffe, #fffffe)

.halyk-corners
  display inline-table
  padding 18px
  background:
    linear-gradient(to right, #14a85d 4px, transparent 4px) 0 0,
    linear-gradient(to right, #14a85d 4px, transparent 4px) 0 100%,
    linear-gradient(to left, #14a85d 4px, transparent 4px) 100% 0,
    linear-gradient(to left, #14a85d 4px, transparent 4px) 100% 100%,
    linear-gradient(to bottom, #14a85d 4px, transparent 4px) 0 0,
    linear-gradient(to bottom, #14a85d 4px, transparent 4px) 100% 0,
    linear-gradient(to top, #14a85d 4px, transparent 4px) 0 100%,
    linear-gradient(to top, #14a85d 4px, transparent 4px) 100% 100%
  background-repeat no-repeat
  background-size 30px 30px
</style>
